import { c as _c } from "react/compiler-runtime";
import type { FC, FormEvent } from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { Button } from '@sbt-web/ui';
import type { RootState } from '@/reducers/index';
import { updatePage } from '@/reducers/search';
import { LISTING_PAGE_LIMIT } from '@/shared/constants';
import ownClasses from './pagination.module.scss';
interface StateProps {
  currentPage: number;
  lastPage: number;
}
interface DispatchProps {
  changePage: (page: number) => void;
}
type Props = StateProps & DispatchProps;
enum ButtonDirection {
  PREV = 'left-arrow',
  NEXT = 'right-arrow',
}
const ArrowLeft: FC = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fillRule="evenodd" d="M9.414 12l6.293-6.293a1 1 0 10-1.414-1.414l-7 7a1 1 0 000 1.414l7 7a1 1 0 001.414-1.414L9.414 12z" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const ArrowRight: FC = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path fillRule="evenodd" d="M14.586 12L8.293 5.707a1 1 0 111.414-1.414l7 7a1 1 0 010 1.414l-7 7a1 1 0 01-1.414-1.414L14.586 12z" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
type PageClick = (e: FormEvent, page: number) => void;
const Placeholder = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className={classnames("button-text", ownClasses["button-like"], ownClasses.placeholder)}>…</span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
type PaginationArrowProps = {
  direction: ButtonDirection;
  onClick: PageClick;
  page: number | null;
  disabled: boolean;
};
const PaginationArrow: FC<PaginationArrowProps> = t0 => {
  const $ = _c(10);
  const {
    direction,
    onClick,
    page,
    disabled: t1
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== direction) {
    t2 = direction === ButtonDirection.NEXT ? <ArrowRight /> : <ArrowLeft />;
    $[0] = direction;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const icon = t2;
  let t3;
  if ($[2] !== onClick || $[3] !== page) {
    t3 = e => {
      if (page) {
        onClick(e, page);
      }
    };
    $[2] = onClick;
    $[3] = page;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = direction === ButtonDirection.NEXT ? "Andare alla prossima pagina" : "Andare alla pagina precedente";
  let t5;
  if ($[5] !== disabled || $[6] !== icon || $[7] !== t3 || $[8] !== t4) {
    t5 = <Button design="outline" icon={icon} disabled={disabled} onClick={t3} aria-label={t4} />;
    $[5] = disabled;
    $[6] = icon;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};
interface PageNumberProps {
  page: number;
  onClick: PageClick;
}
const PageNumber: FC<PageNumberProps> = t0 => {
  const $ = _c(10);
  const {
    page,
    onClick
  } = t0;
  let t1;
  if ($[0] !== onClick || $[1] !== page) {
    t1 = e => {
      onClick(e, page);
    };
    $[0] = onClick;
    $[1] = page;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["pagination__btn", ownClasses["pagination-button"]];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = `Andare a pagina ${page}`;
  let t4;
  if ($[4] !== page) {
    t4 = page.toString();
    $[4] = page;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t1 || $[7] !== t3 || $[8] !== t4) {
    t5 = <Button design="text" onClick={t1} classes={t2} aria-label={t3}>{t4}</Button>;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};
const CurrentPage: FC<{
  children: string;
}> = t0 => {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = classnames("button-text", ownClasses["current-page"], ownClasses["button-like"]);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <p className={t1}>{children}</p>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const PaginationComponent: FC<Props> = t0 => {
  const $ = _c(32);
  const {
    lastPage,
    currentPage,
    changePage
  } = t0;
  let t1;
  if ($[0] !== changePage) {
    t1 = (e, selectedPage) => {
      e.preventDefault();
      window.scrollTo(0, 0);
      changePage(selectedPage);
    };
    $[0] = changePage;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleSelection = t1;
  if (lastPage === 1 || currentPage > lastPage || currentPage > LISTING_PAGE_LIMIT) {
    return null;
  }
  const onFirstPage = currentPage === 1;
  const onLastPage = currentPage === lastPage || currentPage === LISTING_PAGE_LIMIT;
  const previousPage = onFirstPage ? null : currentPage - 1;
  const nextPage = onLastPage ? null : currentPage + 1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = classnames("pagination-container", ownClasses["pagination-container"]);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  let t4;
  if ($[3] !== handleSelection || $[4] !== onFirstPage) {
    t3 = <PaginationArrow direction={ButtonDirection.PREV} disabled={onFirstPage} onClick={handleSelection} page={1} />;
    t4 = onFirstPage ? null : <PageNumber onClick={handleSelection} page={1} />;
    $[3] = handleSelection;
    $[4] = onFirstPage;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  let t5;
  if ($[7] !== previousPage) {
    t5 = previousPage && previousPage > 2 ? <Placeholder /> : null;
    $[7] = previousPage;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== currentPage || $[10] !== handleSelection || $[11] !== previousPage) {
    t6 = previousPage !== null && currentPage !== 2 ? <PageNumber onClick={handleSelection} page={previousPage} /> : null;
    $[9] = currentPage;
    $[10] = handleSelection;
    $[11] = previousPage;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== currentPage) {
    t7 = currentPage.toString();
    $[13] = currentPage;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t7) {
    t8 = <CurrentPage>{t7}</CurrentPage>;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== handleSelection || $[18] !== nextPage) {
    t9 = nextPage !== null ? <PageNumber onClick={handleSelection} page={nextPage} /> : null;
    $[17] = handleSelection;
    $[18] = nextPage;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== handleSelection || $[21] !== nextPage || $[22] !== onLastPage) {
    t10 = <PaginationArrow direction={ButtonDirection.NEXT} disabled={onLastPage} onClick={handleSelection} page={nextPage} />;
    $[20] = handleSelection;
    $[21] = nextPage;
    $[22] = onLastPage;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t3 || $[26] !== t4 || $[27] !== t5 || $[28] !== t6 || $[29] !== t8 || $[30] !== t9) {
    t11 = <nav className={t2}>{t3}{t4}{t5}{t6}{t8}{t9}{t10}</nav>;
    $[24] = t10;
    $[25] = t3;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
    $[29] = t8;
    $[30] = t9;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  return t11;
};
const mapStateToProps = (state: Pick<RootState, 'search' | 'items'>): StateProps => {
  return {
    currentPage: state.search.page,
    lastPage: state.items.totalPages
  };
};
const PaginationSelector = connect<StateProps, DispatchProps, unknown, RootState>(mapStateToProps, dispatch => ({
  changePage: (page: number) => {
    dispatch(updatePage(page));
  }
}))(PaginationComponent);
export default PaginationSelector;
export { PaginationComponent, mapStateToProps };